import { Directive, ElementRef, HostListener, Input, Self } from '@angular/core';
import { NbTooltipDirective } from '@nebular/theme';

/**
 * Reveals an element's full text in a styled nb-tooltip, but only when
 * the element's own content is visually truncated (ellipsis/overflow) —
 * e.g. a narrow table column. Piggybacks on a sibling `nbTooltip`
 * directive on the same host for rendering, timing (Nebular's built-in
 * ~100ms hover delay) and styling; add a bare `nbTooltip` attribute
 * alongside `appTruncateTooltip` so Nebular instantiates it. Truncation
 * is (re-)checked on every `mouseenter`, so it stays correct across
 * re-renders and column-width changes.
 *
 * `[appTruncateTooltip]="text"` supplies the full text explicitly (use
 * whenever the visible text differs from, or is only part of, the value
 * that should appear in the tooltip). Omit the binding to fall back to
 * the element's own trimmed textContent.
 *
 * `[truncateTooltipFallback]="text"` is shown instead when the content
 * is NOT truncated — use this to keep an existing hint (e.g. "click to
 * edit") on the same host without stacking a second, overlapping
 * `nbTooltip` on a parent element. Omit it to simply show no tooltip
 * when the content already fits.
 */
@Directive({
  selector: '[appTruncateTooltip]',
})
export class TruncateTooltipDirective {
  @Input('appTruncateTooltip') customText?: string;
  @Input() truncateTooltipFallback?: string;

  constructor(
    private readonly el: ElementRef<HTMLElement>,
    @Self() private readonly nbTooltip: NbTooltipDirective
  ) {}

  @HostListener('mouseenter')
  onMouseEnter(): void {
    const element = this.el.nativeElement;
    const isTruncated = element.scrollWidth > element.clientWidth + 1;

    if (isTruncated) {
      // `||` not `??`: a bare `appTruncateTooltip` attribute (no binding) sets
      // customText to '' — treating that as "no custom text" avoids an empty tooltip.
      this.nbTooltip.content = this.customText || element.textContent?.trim() || '';
      this.nbTooltip.disabled = false;
    } else if (this.truncateTooltipFallback) {
      this.nbTooltip.content = this.truncateTooltipFallback;
      this.nbTooltip.disabled = false;
    } else {
      this.nbTooltip.disabled = true;
    }
    this.nbTooltip.rebuild();
  }
}
